import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import {DataGrid} from '@mui/x-data-grid';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {getReportStats, getUserActivityByUser, getUserActivitySummary, getAiActivity} from '../../../../utils/api/analytics';

const PERIODS = [
    {code: '1d', label: '24h'},
    {code: '1w', label: '7d'},
    {code: '30d', label: '30d'},
];

function formatDuration(ms) {
    if (ms == null) return '—';
    const totalSeconds = Math.round(ms / 1000);
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    if (minutes < 60) return `${minutes}m ${totalSeconds % 60}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
}

const AnalyticsPage = () => {
    const [recent, setRecent] = useState('1w');
    const [summary, setSummary] = useState(null);
    const [byUser, setByUser] = useState([]);
    const [reportStats, setReportStats] = useState(null);
    const [aiActivity, setAiActivity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        Promise.all([getUserActivitySummary(recent), getUserActivityByUser(recent), getReportStats(), getAiActivity(recent)])
            .then(([summaryData, byUserData, reportStatsData, aiActivityData]) => {
                if (cancelled) return;
                setSummary(summaryData);
                setByUser(Array.isArray(byUserData?.users) ? byUserData.users : []);
                setReportStats(reportStatsData);
                setAiActivity(aiActivityData);
            })
            .catch((e) => {
                if (!cancelled) setError(e.message || 'Failed to load analytics');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [recent]);

    const dauChart = useMemo(
        () => (summary?.dailyActiveUsers || []).map((d) => ({day: d.day, count: d.count})),
        [summary],
    );
    const uploadsChart = useMemo(
        () => (summary?.uploadsPerDay || []).map((d) => ({day: d.day, count: d.count})),
        [summary],
    );
    const searchesChart = useMemo(
        () => (summary?.searchesPerDay || []).map((d) => ({day: d.day, count: d.count})),
        [summary],
    );

    const aiChatChart = useMemo(
        () => (aiActivity?.chatCallsPerDay || []).map((d) => ({day: d.day, count: d.count})),
        [aiActivity],
    );

    const aiUserRows = useMemo(
        () =>
            (aiActivity?.byUser || []).map((u, i) => ({
                id: u.userId || i,
                name: u.name,
                role: u.role,
                chatCalls: u.chatCalls,
                agenticSections: u.agenticSections,
                tokens: u.tokens,
                costUsd: u.costUsd != null ? Number(u.costUsd).toFixed(4) : '—',
            })),
        [aiActivity],
    );

    const topReportRows = useMemo(
        () =>
            (summary?.topReports || []).map((r, i) => ({
                id: r.reportId || i,
                title: r.title || r.reportId,
                avgMs: r.avgMs,
                openCount: r.openCount,
            })),
        [summary],
    );

    const reportColumns = [
        {field: 'title', headerName: 'Report', flex: 1, minWidth: 220},
        {
            field: 'avgMs',
            headerName: 'Avg time',
            width: 140,
            valueFormatter: (value) => formatDuration(value),
        },
        {field: 'openCount', headerName: 'Opens', width: 100, type: 'number'},
    ];

    const userRows = useMemo(
        () =>
            byUser.map((u, i) => ({
                id: u.userId || i,
                name: u.name,
                role: u.role,
                totalHours: u.totalHours,
                hoursThisWeek: u.hoursThisWeek,
                searches: u.searches,
                reportsAuthored: u.reportsAuthored,
                reportsCollaborated: u.reportsCollaborated,
            })),
        [byUser],
    );

    const userTotals = useMemo(() => {
        const round1 = (n) => Math.round(n * 10) / 10;
        return byUser.reduce(
            (acc, u) => ({
                totalHours: round1(acc.totalHours + (u.totalHours || 0)),
                searches: acc.searches + (u.searches || 0),
                reportsAuthored: acc.reportsAuthored + (u.reportsAuthored || 0),
                reportsCollaborated: acc.reportsCollaborated + (u.reportsCollaborated || 0),
            }),
            {totalHours: 0, searches: 0, reportsAuthored: 0, reportsCollaborated: 0},
        );
    }, [byUser]);

    const userColumns = [
        {field: 'name', headerName: 'User', flex: 1, minWidth: 160},
        {field: 'role', headerName: 'Role', width: 200, renderCell: ({value}) => (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{py: 0.5}}>
                {(value || 'unknown').split(', ').map(r => (
                    <Chip key={r} label={r} size="small" variant="outlined"/>
                ))}
            </Stack>
        )},
        {field: 'totalHours', headerName: 'Total Hours', width: 130, type: 'number'},
        {field: 'hoursThisWeek', headerName: 'Hours This Week', width: 150, type: 'number'},
        {field: 'searches', headerName: 'Searches', width: 120, type: 'number'},
        {field: 'reportsAuthored', headerName: 'Reports Authored', width: 160, type: 'number'},
        {field: 'reportsCollaborated', headerName: 'Reports Collaborated', width: 190, type: 'number'},
    ];

    return (
        <Stack p={2} spacing={3} overflow={'auto'}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">Analytics</Typography>
                <ButtonGroup size="small" variant="outlined">
                    {PERIODS.map((p) => (
                        <Button
                            key={p.code}
                            variant={recent === p.code ? 'contained' : 'outlined'}
                            onClick={() => setRecent(p.code)}
                        >
                            {p.label}
                        </Button>
                    ))}
                </ButtonGroup>
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}

            {loading && !summary ? (
                <Stack alignItems="center" justifyContent="center" sx={{minHeight: 240}}>
                    <CircularProgress/>
                </Stack>
            ) : (
                <>
                    <Paper variant="outlined" sx={{p: 2, borderRadius: 2}}>
                        <Typography fontWeight={600} variant="body1" gutterBottom>Summary</Typography>
                        <Stack direction={'row'} justifyContent={'stretch'} spacing={2} flexWrap="wrap">
                            <Metric label="Total Hours" value={userTotals.totalHours}/>
                            <Metric label="Total Searches" value={userTotals.searches}/>
                            <Metric label="Reports Authored" value={userTotals.reportsAuthored}/>
                            <Metric label="Reports Collaborated" value={userTotals.reportsCollaborated}/>
                            <Metric
                                label="Charts Created"
                                value={summary?.chartsCreated
                                    ?? summary?.creationsByType?.whiteboard_created
                                    ?? 0}
                            />
                            <Metric label="AI Chat Calls" value={aiActivity?.totalChatCalls ?? 0}/>
                            <Metric label="Agentic Sections" value={aiActivity?.totalAgenticSections ?? 0}/>
                            <Metric label="Total Tokens" value={(aiActivity?.totalTokens ?? 0).toLocaleString()}/>
                        </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={{p: 2, borderRadius: 2}}>
                        <Typography fontWeight={600} variant="body1" gutterBottom>Report Pipeline</Typography>
                        <Stack direction={'row'} justifyContent={'stretch'} spacing={2} flexWrap="wrap">
                            <Metric label="Drafts (WIP)" value={reportStats?.drafts ?? 0}/>
                            <Metric label="Completed FINTELs" value={reportStats?.completedFintels ?? 0}/>
                            <Metric label="Tactical SITREP" value={reportStats?.tacticalSitrep ?? 0}/>
                            <Metric label="Strategic SITREP" value={reportStats?.strategicSitrep ?? 0}/>
                            <Metric label="Pitch" value={reportStats?.pitch ?? 0}/>
                            <Metric label="Analytic" value={reportStats?.analytic ?? 0}/>
                        </Stack>
                    </Paper>

                    <Stack direction={'row'} justifyContent={'stretch'} spacing={2} flexWrap="wrap">
                        <Metric label="Avg Session Duration" value={formatDuration(summary?.avgSessionDurationMs)}/>
                        <Metric label="Avg Time on Report" value={formatDuration(summary?.avgTimeOnReportMs)}/>
                        <Metric label="Active Users" value={summary?.activeUsers ?? 0}/>
                        <Metric label="File Uploads" value={summary?.fileUploads ?? 0}/>
                        <Metric label="Searches" value={summary?.searches ?? 0}/>
                    </Stack>

                    <Paper variant="outlined" sx={{p: 2, borderRadius: 2, height: 360}}>
                        <Typography fontWeight={600} variant="body1" mb={2} gutterBottom>Daily Active Users</Typography>
                        {dauChart.length === 0 ? (
                            <EmptyChart label="No activity"/>
                        ) : (
                            <ResponsiveContainer width="100%" height="85%">
                                <LineChart data={dauChart}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                    <XAxis dataKey="day"/>
                                    <YAxis allowDecimals={false}/>
                                    <Tooltip/>
                                    <Line type="monotone" dataKey="count" name="Active users" stroke="#7c3aed"
                                          strokeWidth={2} dot={{r: 3}}/>
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>

                    <Stack direction={{xs: 'column', md: 'row'}} spacing={3}>
                        <Paper variant="outlined" sx={{p: 2, borderRadius: 2, height: 320, flex: 1}}>
                            <Typography fontWeight={600} variant="body1" mb={2} gutterBottom>Uploads per
                                day</Typography>
                            {uploadsChart.length === 0 ? (
                                <EmptyChart label="No uploads"/>
                            ) : (
                                <ResponsiveContainer width="100%" height="80%">
                                    <BarChart data={uploadsChart}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                        <XAxis dataKey="day"/>
                                        <YAxis allowDecimals={false}/>
                                        <Tooltip/>
                                        <Bar dataKey="count" name="Uploads" fill="#1976d2" radius={[4, 4, 0, 0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </Paper>

                        <Paper variant="outlined" sx={{p: 2, borderRadius: 2, height: 320, flex: 1}}>
                            <Typography fontWeight={600} variant="body1" mb={2} gutterBottom>Searches per
                                day</Typography>
                            {searchesChart.length === 0 ? (
                                <EmptyChart label="No searches"/>
                            ) : (
                                <ResponsiveContainer width="100%" height="80%">
                                    <BarChart data={searchesChart}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                        <XAxis dataKey="day"/>
                                        <YAxis allowDecimals={false}/>
                                        <Tooltip/>
                                        <Bar dataKey="count" name="Searches" fill="#2e7d32" radius={[4, 4, 0, 0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </Paper>
                    </Stack>

                    <Stack bgcolor={'white'} borderRadius={2} sx={{p: 2}} spacing={2}>
                        <Typography fontWeight={600} variant="body1" gutterBottom>User Activity Table</Typography>
                        <DataGrid
                            rows={userRows}
                            columns={userColumns}
                            density="compact"
                            getRowHeight={() => 'auto'}
                            disableRowSelectionOnClick
                            autoHeight
                            initialState={{pagination: {paginationModel: {pageSize: 100}}}}
                            pageSizeOptions={[25, 50, 100]}
                            localeText={{noRowsLabel: 'No user activity yet'}}
                        />
                    </Stack>

                    <Stack bgcolor={'white'} borderRadius={2} sx={{p: 2}} spacing={2}>
                        <Typography fontWeight={600} variant="body1" gutterBottom>Top Reports by Time Spent</Typography>
                        <DataGrid
                            rows={topReportRows}
                            columns={reportColumns}
                            density="compact"
                            disableRowSelectionOnClick
                            autoHeight
                            hideFooter={topReportRows.length <= 100}
                            localeText={{noRowsLabel: 'No report activity yet'}}
                        />
                    </Stack>

                    {/* ── AI & Chat Activity ─────────────────────────────── */}
                    <Paper variant="outlined" sx={{p: 2, borderRadius: 2}}>
                        <Typography fontWeight={600} variant="body1" gutterBottom>AI &amp; Chat Activity</Typography>

                        <Paper variant="outlined" sx={{p: 2, borderRadius: 2, height: 280, mb: 2}}>
                            <Typography variant="body2" color="text.secondary" mb={1}>AI Chat Calls per Day</Typography>
                            {aiChatChart.length === 0 ? (
                                <EmptyChart label="No AI chat activity"/>
                            ) : (
                                <ResponsiveContainer width="100%" height="90%">
                                    <LineChart data={aiChatChart}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                        <XAxis dataKey="day"/>
                                        <YAxis allowDecimals={false}/>
                                        <Tooltip/>
                                        <Line type="monotone" dataKey="count" name="Chat calls" stroke="#d97706"
                                              strokeWidth={2} dot={{r: 3}}/>
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </Paper>

                        <Typography variant="body2" fontWeight={600} gutterBottom>Per-User AI Usage</Typography>
                        <DataGrid
                            rows={aiUserRows}
                            columns={[
                                {field: 'name', headerName: 'User', flex: 1, minWidth: 160},
                                {field: 'role', headerName: 'Role', width: 200, renderCell: ({value}) => (
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{py: 0.5}}>
                                        {(value || 'unknown').split(', ').map(r => (
                                            <Chip key={r} label={r} size="small" variant="outlined"/>
                                        ))}
                                    </Stack>
                                )},
                                {field: 'chatCalls', headerName: 'Chat Calls', width: 120, type: 'number'},
                                {field: 'agenticSections', headerName: 'Agentic Sections', width: 160, type: 'number'},
                                {field: 'tokens', headerName: 'Tokens', width: 120, type: 'number'},
                            ]}
                            density="compact"
                            getRowHeight={() => 'auto'}
                            disableRowSelectionOnClick
                            autoHeight
                            initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                            pageSizeOptions={[25, 50, 100]}
                            localeText={{noRowsLabel: 'No AI activity yet'}}
                        />
                    </Paper>
                </>
            )}
        </Stack>
    );
};

const Metric = ({label, value}) => (
    <Paper variant="outlined" sx={{p: 1.5, textAlign: 'center', flexGrow: 1, minWidth: 140}}>
        <Typography variant="body2" sx={{letterSpacing: 0.5}}>{label}</Typography>
        <Typography variant="h5" color="primary" sx={{fontWeight: 600}}>{value}</Typography>
    </Paper>
);

const EmptyChart = ({label}) => (
    <Box sx={{height: '85%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
);

export default AnalyticsPage;
